/* @flow */
import PromptStore from 'stores/PromptStore';
import GroupStore from 'groups/stores/GroupStore';
import GroupActions from 'groups/actions/GroupActions';
import { PromptNames } from 'constants/PromptConstants';
import CoachingConstants from 'coaching/CoachingConstants';
import Cookies from 'lib/Cookies';

function needsSchool(user) {
  const groupsJoined = GroupStore.getGroupsJoinedDuringSetup();
  const joinedGroupWithSchool = groupsJoined && groupsJoined.some(group => group.getOrganization());

  return user.getNeeds().organization && !joinedGroupWithSchool;
}

function needsGroup(user) {
  return (
    !PromptStore.hasDismissed(PromptNames.NUX_SETUP_CLASS) &&
    user.needsGroups() && // User object isn't updated when a group is created
    GroupStore.getOwnedGroups().length === 0
  );
}

function needsSignature(user) {
  return (
    user.getNeeds().signature &&
    !user.getSignature() &&
    user.getSignInCount() <= 2 &&
    !PromptStore.hasDismissed(PromptNames.NUX_SETUP_SIGNATURE)
  );
}

function needsSmsDevice(user) {
  if (
    user.hasFeature('claim_devices.web.july-31') ||
    user.getRole() === 'student' ||
    user.getRole() === 'parent'
  ) {
    return (
      user.getSignInCount() <= 2 &&
      user.hasSmsSupported() &&
      // The user isn't updated after this setup step completes, but it works anyway because of the prompt. If removing the prompt, you'll need to retetch the user after they finish this setup step.
      !user.hasSmsDevice() &&
      !PromptStore.hasDismissed(PromptNames.NUX_SETUP_SMS_DEVICE)
    );
  }

  return false;
}

function needsSubscription(user) {
  return (
    user.getSignInCount() <= 1 &&
    user.getNeeds().subscriptions &&
    !PromptStore.hasDismissed(PromptNames.NUX_SETUP_FIRST_SUBSCRIPTION) &&
    !isIntendedCoachingUser()
  );
}

function needsFullName(user) {
  return user.getNeeds().full_name;
}

function needsEmail(user) {
  if (user.hasFeature('claim_devices.web.july-31') || user.getRole() === 'teacher') {
    return !user.hasEmail() && !PromptStore.hasDismissed(PromptNames.NUX_SETUP_EMAIL_DEVICE);
  }

  return false;
}

function needRole(user) {
  return user.getNeeds().role;
}

function needsPassword(user) {
  return user.getNeeds().password;
}

function needPIIConsent(user) {
  return user.getNeeds().pii_consent;
}

// student always need a birthdate (new 2018 ikeepsafe requirement)
function needStudentBirthdate(user) {
  return !user.getBirthdate();
}

function needsParentEmail(user) {
  return user.getNeeds().parent_email;
}

function needsRosterAll(user) {
  return user.getNeeds().roster_all;
}

function needsRosterChoose(user) {
  return user.getNeeds().roster_choose;
}

function needsRosterConfirm(user) {
  return user.getNeeds().roster_confirm;
}

export function isIntendedCoachingUser() {
  return Cookies.get(CoachingConstants.IS_INTENDED_COACHING_USER_COOKIE) === 'true';
}

function clearSetupData() {
  // Prevents "Can't dispatch within a dispatch" error when booting the app. The other dispatch is loading groups.
  setTimeout(() => {
    GroupActions.clearGroupsJoinedDuringSetup();
  }, 0);
}

type Steps = { if: (user: *) => boolean, step: string };

// prettier-ignore
const prerequisites: Array<Steps> = [
  { if: needPIIConsent, step: '/setup/pii'      },
  { if: needsPassword,  step: '/setup/password' },
  { if: needsFullName,  step: '/setup/name'     },
  { if: needRole,       step: '/setup/role'     },
];

// prettier-ignore
const steps: { [string]: Array<Steps> } = {
  teacher: [
    { if: needsSignature,         step: '/setup/signature'      },
    { if: needsEmail,             step: '/setup/email'          },
    { if: needsSmsDevice,         step: '/setup/device/sms'     },
    { if: needsGroup,             step: '/setup/class'          },
    { if: needsSchool,            step: '/setup/school'         },
    { if: needsRosterConfirm,     step: '/setup/roster/confirm' },
    { if: needsRosterAll,         step: '/setup/roster/all'     },
    { if: needsRosterChoose,      step: '/setup/roster/choose'  },
    { if: isIntendedCoachingUser, step: CoachingConstants.COACHING_PURCHASE_REVIEW_SETUP_FINISHED_ROUTE }
  ],
  student: [
    { if: needsFullName,          step: '/setup/name'             },
    { if: needStudentBirthdate,   step: '/setup/birthdate'        },
    { if: needsParentEmail,       step: '/setup/parent_email'     },
    { if: needsEmail,             step: '/setup/email'            },
    { if: needsSmsDevice,         step: '/setup/device/sms'       },
    { if: needsSubscription,      step: '/setup/subscriptions'    },
    { if: needsRosterConfirm,     step: '/setup/roster/confirm'   },
    { if: needsRosterAll,         step: '/setup/roster/all'       },
    { if: needsRosterChoose,      step: '/setup/roster/choose'    },
    { if: isIntendedCoachingUser, step: CoachingConstants.COACHING_PURCHASE_REVIEW_SETUP_FINISHED_ROUTE }
  ],
  parent: [
    { if: needsEmail,             step: '/setup/email'          },
    { if: needsSmsDevice,         step: '/setup/device/sms'     },
    { if: needsSubscription,      step: '/setup/subscriptions'  },
    { if: needsRosterConfirm,     step: '/setup/roster/confirm' },
    { if: needsRosterAll,         step: '/setup/roster/all'     },
    { if: needsRosterChoose,      step: '/setup/roster/choose'  },
    { if: isIntendedCoachingUser, step: CoachingConstants.COACHING_PURCHASE_REVIEW_SETUP_FINISHED_ROUTE }
  ],
};

function getStepsForRole(role: ?string): Array<Steps> {
  return [...prerequisites, ...(role ? steps[role] || [] : [])];
}

export default {
  getNextEligibleStep: (user: *) => {
    const stepsForRole = getStepsForRole(user.getRole());
    const eligibleStep = stepsForRole.find(step => step.if(user));

    if (eligibleStep) {
      return eligibleStep.step;
    }

    clearSetupData();
    return undefined;
  },
};
